import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Row, Col, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { loadAccountTypes } from "store/slices/AccountSlice";
import { loadCountries } from "store/slices/MasterSlice";
import * as adminactions from "store/actions/AuthActions";
import { useNotification } from "NotificationContext";

/**
 * Lightweight modal to quickly create an Account from within the Invoice form.
 * Only captures the essential fields; on success the created account is passed
 * back to the parent via onCreated so it can be selected immediately.
 */
const QuickAddAccountModal = ({ open, onCancel, onCreated }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const notify = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const account_types = useSelector((store) => store.accounts?.account_types);
  const country_options = useSelector((store) => store.master?.countries);

  useEffect(() => {
    if (open) {
      if (!account_types || !account_types.choices) {
        dispatch(loadAccountTypes());
      }
      if (!country_options || (Array.isArray(country_options) && country_options.length === 0)) {
        dispatch(loadCountries());
      }
    }
  }, [open]);

  useEffect(() => {
    // Apply the backend default account type for fresh forms.
    if (open && account_types?.default) {
      form.setFieldValue("account_type", account_types.default);
    }
  }, [open, account_types]);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // validation errors already shown inline
    }

    // The accounts dropdown labels options by `name`; fall back to the legal
    // name so the newly created account is readable in the Select.
    const payload = {
      ...values,
      name: values.legal_name_of_business,
    };

    setSubmitting(true);
    try {
      const created = await adminactions.addAccounts(payload);
      notify("success", "Account created");
      form.resetFields();
      onCreated?.(created);
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        typeof data === "object" && data !== null
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join("; ")
          : "Failed to create account";
      notify("error", "Add operation failed", msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel?.();
  };

  return (
    <Modal
      title="Add Account"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Create"
      confirmLoading={submitting}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Legal name of business"
              name="legal_name_of_business"
              rules={[{ required: true, message: "Please enter the legal name of business" }]}
            >
              <Input placeholder="Legal name of business" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Account Type"
              name="account_type"
              rules={[{ required: true, message: "Please select an account type" }]}
            >
              <Select
                showSearch
                placeholder="Select Account Type"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase?.() ?? "").includes(input?.toLowerCase?.())
                }
                loading={!(Array.isArray(account_types?.choices) && account_types?.choices?.length > 0)}
                options={account_types?.choices || []}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="GST Number" name="gst_number">
              <Input maxLength={15} showCount placeholder="GST Number" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 0 }}>
          Billing Address
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Address line 1" name="billing_address1">
              <Input placeholder="Address line 1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Address line 2" name="billing_address2">
              <Input placeholder="Address line 2" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="City" name="billing_city">
              <Input placeholder="City" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="State" name="billing_state">
              <Input placeholder="State" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Postal Code" name="billing_postal_code">
              <Input placeholder="Postal Code" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Country" name="billing_country">
              <Select
                showSearch
                allowClear
                placeholder="Search to Select"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase?.() ?? "").includes(input?.toLowerCase?.())
                }
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                loading={!(Array.isArray(country_options) && country_options?.length > 0)}
                options={country_options?.map?.((opt) => ({
                  label: `${opt.name}`,
                  value: opt.id,
                })) || []}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Billing Phone" name="billing_phone">
              <Input maxLength={20} showCount placeholder="Billing Phone" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default QuickAddAccountModal;
